// ─── GlobalCombinedUploadModal ─────────────────────────────────────────────
// Replace the entire file with this version.
// Only the sections that changed are annotated with // ← CHANGED

import {
  UploadCloud,
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Globe,
  FileText,
  Users,
  BookOpen,
  AlertTriangle,
  Calendar,
  ChevronRight,
  Upload,
} from "lucide-react";
import GlobalUploadGuideButton from "../GlobalUploadGuideButton";
import { useState, useRef } from "react";
import JSZip from "jszip";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { supabase } from "lib/supabase";
import { useAuth } from "contexts/AuthContext";
import { uploadBulkResumes } from "services/bulkresumeService";

const API_BASE = import.meta.env.VITE_AI_BACKEND_URL;
const MAX_SIZE = 200 * 1024 * 1024;

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

const isSmeRow = (row) =>
  row.is_sme === true ||
  row.is_sme === 1 ||
  String(row.is_sme || "").toLowerCase() === "true";

const VALID_UNITS = new Set(["pct", "mult", "flat"]);
const UNIT_ALIASES = {
  pct: "pct",
  percent: "pct",
  percentage: "pct",
  "%": "pct",
  mult: "mult",
  multiplier: "mult",
  multiply: "mult",
  flat: "flat",
  fixed: "flat",
};
const normalizeUnit = (raw) => {
  const key = String(raw || "")
    .trim()
    .toLowerCase();
  return UNIT_ALIASES[key] || (VALID_UNITS.has(key) ? key : "pct");
};

const isPlaceholderColumn = (key) => /^_\d+$/.test(key.trim());

const detectConflicts = (rows, field, existingNames) => {
  const canonical = {};
  (existingNames || []).forEach((n) => {
    const norm = String(n ?? "")
      .trim()
      .toLowerCase();
    if (norm && !(norm in canonical)) canonical[norm] = String(n ?? "");
  });
  const conflicts = [],
    seenVariants = new Set();
  rows.forEach((row) => {
    const raw = row[field];
    if (!raw) return;
    const rawStr = String(raw),
      norm = rawStr.trim().toLowerCase();
    if (!norm) return;
    if (!(norm in canonical)) {
      canonical[norm] = rawStr;
      return;
    }
    const canonicalRaw = canonical[norm];
    if (rawStr !== canonicalRaw && !seenVariants.has(rawStr)) {
      seenVariants.add(rawStr);
      conflicts.push({
        id: `${field}::${norm}::${rawStr}`,
        type: field,
        canonicalName: canonicalRaw,
        variantName: rawStr,
      });
    }
  });
  return conflicts;
};

const detectRoleMismatches = (personnelRows, roleSheetRows) => {
  const groupRoleMap = {};
  roleSheetRows.forEach((r) => {
    if (!r.Group || !r.Role) return;
    const gKey = String(r.Group).trim().toLowerCase();
    if (!groupRoleMap[gKey]) groupRoleMap[gKey] = new Set();
    groupRoleMap[gKey].add(String(r.Role).trim().toLowerCase());
  });
  const mismatches = [];
  personnelRows.forEach((row, idx) => {
    if (!row.Group || !row.Role) return;
    const gKey = String(row.Group).trim().toLowerCase();
    const rKey = String(row.Role).trim().toLowerCase();
    const validRoles = groupRoleMap[gKey];
    if (validRoles && !validRoles.has(rKey)) {
      mismatches.push({
        idx,
        name: row.Name || "—",
        email: row.Email || "—",
        originalGroup: row.Group,
        originalRole: row.Role,
        validRolesForGroup: roleSheetRows
          .filter((r) => String(r.Group).trim().toLowerCase() === gKey)
          .map((r) => String(r.Role).trim()),
        allGroups: [
          ...new Set(
            roleSheetRows.map((r) => String(r.Group).trim()).filter(Boolean),
          ),
        ],
      });
    }
  });
  return mismatches;
};

const detectLevelMismatches = (personnelRows, levelSheetRows) => {
  const groupLevelMap = {};
  levelSheetRows.forEach((r) => {
    if (!r.Group || (r.Level === undefined && r.Level !== 0)) return;
    const gKey = String(r.Group).trim().toLowerCase();
    const lKey = String(r.Level).trim().toLowerCase();
    if (!groupLevelMap[gKey]) groupLevelMap[gKey] = new Set();
    groupLevelMap[gKey].add(lKey);
  });
  const mismatches = [];
  personnelRows.forEach((row, idx) => {
    if (!row.Group || !row.Level) return;
    const gKey = String(row.Group).trim().toLowerCase();
    const lKey = String(row.Level).trim().toLowerCase();
    const validLevels = groupLevelMap[gKey];
    if (validLevels && !validLevels.has(lKey)) {
      mismatches.push({
        idx,
        name: row.Name || "—",
        email: row.Email || "—",
        originalGroup: row.Group,
        originalLevel: row.Level,
        validLevelsForGroup: levelSheetRows
          .filter((r) => String(r.Group).trim().toLowerCase() === gKey)
          .map((r) => String(r.Level).trim()),
      });
    }
  });
  return mismatches;
};

const buildResolutionMap = (conflicts, resolutions) => {
  const map = {};
  conflicts.forEach((c) => {
    map[`${c.type}::${c.variantName}`] = resolutions[c.id] || "skip";
  });
  return map;
};

const masterKeyFor = (rawName, type, resolutionMap) => {
  if (!rawName) return null;
  const str = String(rawName);
  const resolution = resolutionMap[`${type}::${str}`];
  if (resolution === "create") return `__variant__:${type}:${str}`;
  return str.trim().toLowerCase();
};

const masterNameFor = (rawName, type, resolutionMap) => {
  const str = String(rawName);
  const resolution = resolutionMap[`${type}::${str}`];
  return resolution === "create" ? str : str.trim();
};

// ─── Parse XLSX ───────────────────────────────────────────────────────────────

const parseMastersXlsx = (arrayBuffer) => {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const sheetToRows = (name) => {
    const ws = wb.Sheets[name];
    if (!ws) return [];
    return XLSX.utils.sheet_to_json(ws, { defval: "" });
  };
  return {
    masters: sheetToRows("Masters"),
    roles: sheetToRows("Role"),
    levels: sheetToRows("Level"),
    personnel: sheetToRows("Personnel"),
    discount: sheetToRows("Discount"),
    burden: sheetToRows("Burden"),
  };
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  if (status === "pending")
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
        Pending
      </span>
    );
  if (status === "running")
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium flex items-center gap-1">
        <Loader2 size={10} className="animate-spin" />
        Running
      </span>
    );
  if (status === "done")
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
        Done
      </span>
    );
  if (status === "error")
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
        Error
      </span>
    );
  if (status === "skipped")
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 font-medium">
        Not in ZIP
      </span>
    );
  if (status === "needs-input")
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
        Needs Input
      </span>
    );
  return null;
};

// ─── Section card ─────────────────────────────────────────────────────────────

const SectionCard = ({ icon: Icon, title, color, status, children }) => {
  const colorMap = {
    blue: "border-blue-200 bg-blue-50/40",
    emerald: "border-emerald-200 bg-emerald-50/40",
    violet: "border-violet-200 bg-violet-50/40",
  };
  const iconMap = {
    blue: "bg-blue-100 text-blue-600",
    emerald: "bg-emerald-100 text-emerald-600",
    violet: "bg-violet-100 text-violet-600",
  };
  const skipped = status === "skipped";
  return (
    <div
      className={`border rounded-xl p-4 transition-all ${skipped ? "border-gray-200 bg-gray-50/40 opacity-50" : colorMap[color]}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${skipped ? "bg-gray-100 text-gray-400" : iconMap[color]}`}
          >
            <Icon size={14} />
          </div>
          <span className="text-sm font-semibold text-foreground">{title}</span>
        </div>
        <StatusBadge status={status} />
      </div>
      {!skipped && children}
    </div>
  );
};

// ─── Conflict resolver ────────────────────────────────────────────────────────

const ConflictResolver = ({ conflicts, resolutions, onChange }) => {
  if (!conflicts.length) return null;
  return (
    <div className="border rounded-lg p-3 bg-yellow-50 border-yellow-200 mb-3">
      <p className="text-xs font-semibold text-yellow-800 mb-2">
        Duplicate names detected — resolve each before importing
      </p>
      <div className="space-y-2">
        {conflicts.map((c) => (
          <div
            key={c.id}
            className="flex items-center justify-between gap-2 bg-white border border-yellow-200 rounded-lg p-2.5"
          >
            <div className="text-xs">
              <span className="font-mono bg-muted px-1 rounded">
                "{c.variantName}"
              </span>{" "}
              looks like{" "}
              <span className="font-mono bg-muted px-1 rounded">
                "{c.canonicalName}"
              </span>
              <span className="text-muted-foreground ml-1">
                (
                {c.type === "group"
                  ? "Group"
                  : c.type === "Role"
                    ? "Role"
                    : c.type === "department"
                      ? "Dept"
                      : "Exp. Level"}
                )
              </span>
            </div>
            <div className="flex gap-1.5 flex-shrink-0">
              {["skip", "create"].map((opt) => (
                <button
                  key={opt}
                  onClick={() => onChange(c.id, opt)}
                  className={`px-2 py-1 rounded text-xs font-medium border transition ${resolutions[c.id] === opt ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                >
                  {opt === "skip" ? "Use existing" : "Create new"}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Role mismatch resolver ───────────────────────────────────────────────────

const RoleMismatchStep = ({
  mismatches,
  roleSheetRows,
  corrections,
  onCorrect,
}) => {
  const groupRoleMap = {};
  roleSheetRows.forEach((r) => {
    if (!r.Group || !r.Role) return;
    const gKey = String(r.Group).trim();
    if (!groupRoleMap[gKey]) groupRoleMap[gKey] = [];
    const role = String(r.Role).trim();
    if (!groupRoleMap[gKey].includes(role)) groupRoleMap[gKey].push(role);
  });
  const allGroups = Object.keys(groupRoleMap);
  return (
    <div className="space-y-2">
      <p className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
        <AlertTriangle size={12} /> Fix role/group mismatches before importing
      </p>
      {mismatches.map((m) => {
        const correction = corrections[m.idx] || {
          group: m.originalGroup,
          role: "",
        };
        const rolesForGroup =
          groupRoleMap[correction.group || m.originalGroup] || [];
        return (
          <div
            key={m.idx}
            className="border border-amber-200 rounded-lg p-3 bg-amber-50"
          >
            <p className="text-xs font-medium mb-2">
              {m.name}{" "}
              <span className="text-muted-foreground">({m.email})</span>
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  Group <span className="text-red-500">*</span>
                </label>
                <select
                  value={correction.group || m.originalGroup}
                  onChange={(e) =>
                    onCorrect(m.idx, { group: e.target.value, role: "" })
                  }
                  className="w-full text-xs border border-border rounded px-2 py-1.5 bg-background"
                >
                  {allGroups.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  value={correction.role || ""}
                  onChange={(e) =>
                    onCorrect(m.idx, { ...correction, role: e.target.value })
                  }
                  className={`w-full text-xs border rounded px-2 py-1.5 bg-background ${!correction.role ? "border-red-300" : "border-border"}`}
                >
                  <option value="">Select role</option>
                  {rolesForGroup.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── SME date step ────────────────────────────────────────────────────────────

const SmeDateStep = ({ smeRows, smeDates, onDateChange }) => (
  <div className="space-y-2">
    <p className="text-xs text-purple-700 font-medium flex items-center gap-1.5">
      <Calendar size={12} /> Collaboration dates required for SME personnel
    </p>
    {smeRows.map((row, idx) => {
      const key = row.Email?.trim().toLowerCase() || String(idx);
      const dates = smeDates[key] || { start: "", end: "" };
      const startErr = dates.end && dates.start && dates.start > dates.end;
      return (
        <div
          key={key}
          className="border rounded-lg p-3 bg-purple-50/40 border-purple-200"
        >
          <p className="text-xs font-medium mb-2">
            {row.Name || "—"}{" "}
            <span className="text-muted-foreground">({row.Email || "—"})</span>
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">
                Start <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dates.start}
                onChange={(e) => onDateChange(key, "start", e.target.value)}
                className={`w-full text-xs border rounded px-2 py-1.5 bg-background ${!dates.start ? "border-red-300" : "border-border"}`}
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">
                End <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dates.end}
                min={dates.start || undefined}
                onChange={(e) => onDateChange(key, "end", e.target.value)}
                className={`w-full text-xs border rounded px-2 py-1.5 bg-background ${!dates.end || startErr ? "border-red-300" : "border-border"}`}
              />
              {startErr && (
                <p className="text-xs text-red-500 mt-0.5">
                  Must be after start
                </p>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

// ─── Org docs file table ──────────────────────────────────────────────────────

const OrgDocsFileTable = ({ files }) => (
  <div className="border rounded-lg overflow-hidden">
    <div className="grid grid-cols-3 bg-muted/30 px-3 py-2 text-xs font-semibold">
      <div>File</div>
      <div>Status</div>
      <div>Error</div>
    </div>
    <div className="max-h-48 overflow-y-auto">
      {files?.map((file) => (
        <div
          key={file.id}
          className="grid grid-cols-3 px-3 py-2 border-t text-xs"
        >
          <div className="truncate pr-2">{file.file_name}</div>
          <div>
            {file.status === "VALID" ? (
              <span className="text-green-700 font-medium">VALID</span>
            ) : (
              <span className="text-red-700 font-medium">INVALID</span>
            )}
          </div>
          <div className="text-red-600 truncate">
            {file.error_message || "—"}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── ← CHANGED: Detailed result components ────────────────────────────────────

// Small stat row used inside result cards
const StatRow = ({ label, value, valueColor = "text-foreground", sub }) => (
  <div className="flex items-start justify-between py-1.5 border-b last:border-0 gap-2">
    <span className="text-xs text-muted-foreground">{label}</span>
    <div className="text-right flex-shrink-0">
      <span className={`text-sm font-bold ${valueColor}`}>{value}</span>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  </div>
);

// Inline skip/error pill list
const SkipReasons = ({ items }) => {
  if (!items?.length) return null;
  return (
    <div className="mt-2 space-y-1">
      {items.map((item, i) => (
        <div
          key={i}
          className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
        >
          <AlertTriangle size={11} className="flex-shrink-0 mt-0.5" />
          <span>{item}</span>
        </div>
      ))}
    </div>
  );
};

// Error list shown at the bottom of a result card
const ErrorList = ({ errors }) => {
  if (!errors?.length) return null;
  return (
    <div className="mt-3 border rounded-lg p-2 bg-red-50 border-red-200 max-h-32 overflow-y-auto">
      <p className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
        <AlertCircle size={11} /> {errors.length} error
        {errors.length !== 1 ? "s" : ""}
      </p>
      {errors.map((e, i) => (
        <p key={i} className="text-xs text-red-600 leading-relaxed">
          {e}
        </p>
      ))}
    </div>
  );
};

// Full masters result card
const MastersResultCard = ({ r }) => {
  const personnelSkipReasons = [];
  if (r.pr.skippedDuplicate > 0)
    personnelSkipReasons.push(
      `${r.pr.skippedDuplicate} skipped — duplicate email already in system`,
    );
  if (r.pr.skippedMissing > 0)
    personnelSkipReasons.push(
      `${r.pr.skippedMissing} skipped — missing required Name or Email`,
    );

  const discountSkipReasons = [];
  if (r.discR.skippedDuplicate > 0)
    discountSkipReasons.push(
      `${r.discR.skippedDuplicate} skipped — already exists for this profile`,
    );
  if (r.discR.skippedMissing > 0)
    discountSkipReasons.push(
      `${r.discR.skippedMissing} skipped — missing Discount_name, Discount_type, or Discount_percent`,
    );

  const burdenSkipReasons = [];
  if (r.burnR.skippedDuplicate > 0)
    burdenSkipReasons.push(
      `${r.burnR.skippedDuplicate} skipped — mapping already exists`,
    );
  if (r.burnR.skippedMissing > 0)
    burdenSkipReasons.push(
      `${r.burnR.skippedMissing} skipped — burden row missing Name`,
    );
  if (r.burnR.skippedUnknownColumn > 0)
    burdenSkipReasons.push(
      `${r.burnR.skippedUnknownColumn} skipped — column doesn't match any burden type`,
    );

  const allErrors = [
    ...(r.gr.errors || []),
    ...(r.rr.errors || []),
    ...(r.lr.errors || []),
    ...(r.btr.errors || []),
    ...(r.dr.errors || []),
    ...(r.elr.errors || []),
    ...(r.pr.errors || []),
    ...(r.discR.errors || []),
    ...(r.burnR.errors || []),
  ];

  return (
    <div className="border rounded-xl p-4 bg-blue-50/40 border-blue-200">
      <p className="text-xs font-semibold text-blue-700 mb-3 flex items-center gap-1.5">
        <BookOpen size={13} /> Masters &amp; Personnel
      </p>

      {/* Reference data */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {[
          {
            label: "Groups created",
            val: r.gr.groupsCreated,
            skip:
              r.gr.groupsSkipped > 0
                ? `${r.gr.groupsSkipped} already existed`
                : null,
            color: "text-blue-700",
          },
          {
            label: "Roles created",
            val: r.rr.rolesCreated,
            skip:
              r.rr.rolesSkipped > 0
                ? `${r.rr.rolesSkipped} already existed`
                : null,
            color: "text-blue-700",
          },
          {
            label: "Levels created",
            val: r.lr.levelsCreated,
            skip:
              r.lr.levelsSkipped > 0
                ? `${r.lr.levelsSkipped} already existed`
                : null,
            color: "text-blue-700",
          },
          {
            label: "Departments",
            val: r.dr.departmentsCreated,
            skip:
              r.dr.departmentsSkipped > 0
                ? `${r.dr.departmentsSkipped} already existed`
                : null,
            color: "text-indigo-700",
          },
          {
            label: "Experience levels",
            val: r.elr.experienceLevelsCreated,
            skip:
              r.elr.experienceLevelsSkipped > 0
                ? `${r.elr.experienceLevelsSkipped} already existed`
                : null,
            color: "text-cyan-700",
          },
          {
            label: "Burden types",
            val: r.btr.burdenTypesCreated,
            skip:
              r.btr.burdenTypesSkipped > 0
                ? `${r.btr.burdenTypesSkipped} already existed`
                : null,
            color: "text-orange-700",
          },
        ].map(({ label, val, skip, color }) => (
          <div
            key={label}
            className="bg-white/70 border border-blue-100 rounded-lg px-3 py-2"
          >
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={`text-lg font-bold ${color}`}>{val}</p>
            {skip && <p className="text-xs text-muted-foreground">{skip}</p>}
          </div>
        ))}
      </div>

      {/* Personnel */}
      <div className="bg-white/70 border border-blue-100 rounded-lg px-3 py-2 mb-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Personnel added</p>
          <p className="text-lg font-bold text-green-700">{r.pr.inserted}</p>
        </div>
        <SkipReasons items={personnelSkipReasons} />
      </div>

      {/* Discounts */}
      <div className="bg-white/70 border border-blue-100 rounded-lg px-3 py-2 mb-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Discount heads added</p>
          <p className="text-lg font-bold text-purple-700">
            {r.discR.inserted}
          </p>
        </div>
        {r.discR.profilesCreated > 0 && (
          <p className="text-xs text-purple-600 mt-1">
            {r.discR.profilesCreated} new profile(s) created
          </p>
        )}
        <SkipReasons items={discountSkipReasons} />
      </div>

      {/* Burdens */}
      <div className="bg-white/70 border border-blue-100 rounded-lg px-3 py-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Burdens added</p>
          <p className="text-lg font-bold text-orange-700">
            {r.burnR.burdensCreated}
          </p>
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-muted-foreground">Burden mappings added</p>
          <p className="text-lg font-bold text-orange-700">
            {r.burnR.inserted}
          </p>
        </div>
        <SkipReasons items={burdenSkipReasons} />
      </div>

      <ErrorList errors={allErrors} />
    </div>
  );
};

// ─── Main modal ───────────────────────────────────────────────────────────────

const GlobalCombinedUploadModal = ({ open, onClose, orgId }) => {
  const { profile } = useAuth();

  const [zipFile, setZipFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [detected, setDetected] = useState(null);

  const [mastersData, setMastersData] = useState(null);
  const [masterConflicts, setMasterConflicts] = useState([]);
  const [masterResolutions, setMasterResolutions] = useState({});
  const [masterMismatches, setMasterMismatches] = useState([]);
  const [masterLevelMismatches, setMasterLevelMismatches] = useState([]); // ← CHANGED: tracked
  const [masterRoleCorrections, setMasterRoleCorrections] = useState({});
  const [smeDates, setSmeDates] = useState({});
  const [existingGroups, setExistingGroups] = useState([]);
  const [existingRoles, setExistingRoles] = useState([]);
  const [existingLevels, setExistingLevels] = useState([]);
  const [existingBurdenTypes, setExistingBurdenTypes] = useState([]);
  const [existingDepartments, setExistingDepartments] = useState([]);
  const [existingExperienceLevels, setExistingExperienceLevels] = useState([]);

  const [masterSubStep, setMasterSubStep] = useState("conflicts");

  const [orgDocsScanResult, setOrgDocsScanResult] = useState(null);
  const [resumesZipBlob, setResumesZipBlob] = useState(null);

  const [phase, setPhase] = useState("upload");
  const [sectionStatus, setSectionStatus] = useState({
    masters: "pending",
    resumes: "pending",
    orgDocs: "pending",
  });
  const [results, setResults] = useState({});

  if (!open) return null;

  // ─── Validation ──────────────────────────────────────────────────────────

  const validateZip = (f) => {
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Only ZIP files are allowed");
      return false;
    }
    if (f.size > MAX_SIZE) {
      toast.error("ZIP exceeds 200 MB");
      return false;
    }
    return true;
  };

  // ─── Parse ZIP ───────────────────────────────────────────────────────────

  const handleFilePick = async (f) => {
    if (!validateZip(f)) return;
    setZipFile(f);
    setParsing(true);
    try {
      const jszip = new JSZip();
      const zip = await jszip.loadAsync(f);
      const paths = Object.keys(zip.files);
      const getFolder = (path) =>
        path
          .split("/")[0]
          .trim()
          .toLowerCase()
          .replace(/[\s_-]/g, "");

      const hasMasters = paths.some(
        (p) =>
          ["master", "masters"].includes(getFolder(p)) && !zip.files[p].dir,
      );
      const hasResumes = paths.some(
        (p) =>
          ["resume", "resumes"].includes(getFolder(p)) && !zip.files[p].dir,
      );
      const hasOrgDocs = paths.some(
        (p) =>
          ["orgdocs", "orgdoc", "orgdocuments", "documents", "docs"].includes(
            getFolder(p),
          ) && !zip.files[p].dir,
      );

      if (hasMasters) {
        const xlsxEntry = Object.entries(zip.files).find(
          ([p, f]) =>
            (p.includes("/masters/") || p.startsWith("masters/")) &&
            !f.dir &&
            p.toLowerCase().endsWith(".xlsx"),
        );
        if (!xlsxEntry) {
          toast.error("masters/ folder must contain an .xlsx file");
          setZipFile(null);
          setParsing(false);
          return;
        }

        const arrayBuffer = await xlsxEntry[1].async("arraybuffer");
        const data = parseMastersXlsx(arrayBuffer);
        const currentOrgId = orgId || profile?.organization_id;

        const [
          { data: groupsData },
          { data: rolesData },
          { data: levelsData },
          { data: burdenTypesData },
          { data: departmentsData },
          { data: experienceLevelsData },
        ] = await Promise.all([
          supabase
            .from("md_groups")
            .select("id, name")
            .eq("organization_id", currentOrgId)
            .eq("is_active", 0),
          supabase
            .from("md_roles")
            .select("id, name")
            .eq("organization_id", currentOrgId)
            .eq("is_active", 0),
          supabase
            .from("md_levels")
            .select("id, name, group_id")
            .eq("organization_id", currentOrgId)
            .eq("is_active", 0),
          supabase
            .from("md_burden_type")
            .select("id, name, unit")
            .eq("org_id", currentOrgId)
            .eq("is_active", 0),
          supabase
            .from("md_department")
            .select("id, name")
            .eq("organization_id", currentOrgId)
            .eq("is_active", 0),
          supabase
            .from("md_experience_level")
            .select("id, name")
            .eq("organization_id", currentOrgId)
            .eq("is_active", 0),
        ]);

        const conflicts = [
          ...detectConflicts(
            data.masters,
            "group",
            (groupsData || []).map((g) => g.name),
          ),
          ...detectConflicts(
            data.roles,
            "Role",
            (rolesData || []).map((r) => r.name),
          ),
          ...detectConflicts(
            data.masters,
            "department",
            (departmentsData || []).map((d) => d.name),
          ),
          ...detectConflicts(
            data.masters,
            "experience_level",
            (experienceLevelsData || []).map((x) => x.name),
          ),
        ];

        const mismatches = detectRoleMismatches(data.personnel, data.roles);
        const levelMismatches = detectLevelMismatches(
          data.personnel,
          data.levels,
        ); // ← CHANGED

        const initialDates = {};
        data.personnel.filter(isSmeRow).forEach((row) => {
          const key = row.Email?.trim().toLowerCase() || "";
          if (!key) return;
          initialDates[key] = {
            start: row.collaborator_start_date || "",
            end: row.collaborator_end_date || "",
          };
        });

        setMastersData(data);
        setMasterConflicts(conflicts);
        setMasterResolutions({});
        setMasterMismatches(mismatches);
        setMasterLevelMismatches(levelMismatches); // ← CHANGED
        setMasterRoleCorrections({});
        setSmeDates(initialDates);
        setExistingGroups(groupsData || []);
        setExistingRoles(rolesData || []);
        setExistingLevels(levelsData || []);
        setExistingBurdenTypes(burdenTypesData || []);
        setExistingDepartments(departmentsData || []);
        setExistingExperienceLevels(experienceLevelsData || []);

        if (conflicts.length > 0) setMasterSubStep("conflicts");
        else if (mismatches.length > 0) setMasterSubStep("role-mismatch");
        else if (data.personnel.filter(isSmeRow).length > 0)
          setMasterSubStep("sme-dates");
        else setMasterSubStep("ready");

        const needsInput =
          conflicts.length > 0 ||
          mismatches.length > 0 ||
          data.personnel.filter(isSmeRow).length > 0;
        setSectionStatus((s) => ({
          ...s,
          masters: needsInput ? "needs-input" : "pending",
        }));
      }

      if (hasResumes) {
        const resumeFiles = Object.entries(zip.files).filter(
          ([p, f]) =>
            (p.includes("/resumes/") || p.startsWith("resumes/")) && !f.dir,
        );
        const resumeZip = new JSZip();
        for (const [p, f] of resumeFiles) {
          const filename = p.replace("resumes/", "");
          if (!filename) continue;
          resumeZip.file(filename, await f.async("arraybuffer"));
        }
        setResumesZipBlob(await resumeZip.generateAsync({ type: "blob" }));
      }

      if (hasOrgDocs) {
        setSectionStatus((s) => ({ ...s, orgDocs: "running" }));
        const orgDocsFiles = Object.entries(zip.files).filter(
          ([p, f]) =>
            (p.includes("/org-docs/") || p.startsWith("org-docs/")) && !f.dir,
        );
        const orgDocsZip = new JSZip();
        for (const [p, f] of orgDocsFiles) {
          const filename = p.replace("org-docs/", "");
          if (!filename) continue;
          orgDocsZip.file(filename, await f.async("arraybuffer"));
        }
        const orgDocsBlob = await orgDocsZip.generateAsync({ type: "blob" });
        const orgDocsBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(orgDocsBlob);
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
        });
        const { data: scanData, error: scanError } =
          await supabase.functions.invoke("bulk-scan", {
            body: {
              orgId: orgId || profile?.organization_id,
              zipBase64: orgDocsBase64,
            },
          });
        if (scanError)
          throw new Error(`Org-docs scan failed: ${scanError.message}`);
        setOrgDocsScanResult(scanData);
        setSectionStatus((s) => ({ ...s, orgDocs: "pending" }));
      }

      setDetected({ hasMasters, hasResumes, hasOrgDocs });
      setSectionStatus((s) => ({
        ...s,
        masters: hasMasters ? s.masters : "skipped",
        resumes: hasResumes ? "pending" : "skipped",
        orgDocs: hasOrgDocs ? s.orgDocs : "skipped",
      }));
      setPhase("review");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to parse ZIP");
      setZipFile(null);
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFilePick(f);
  };

  // ─── Masters sub-step nav ─────────────────────────────────────────────────

  const effectivePersonnel = (mastersData?.personnel || []).map((row, idx) => {
    const correction = masterRoleCorrections[idx];
    if (!correction) return row;
    return {
      ...row,
      Group: correction.group || row.Group,
      Role: correction.role || row.Role,
    };
  });

  const smeRows = effectivePersonnel.filter(isSmeRow);
  const allConflictsResolved = masterConflicts.every(
    (c) => masterResolutions[c.id],
  );
  const allMismatchesFixed = masterMismatches.every((m) => {
    const c = masterRoleCorrections[m.idx];
    return c && c.group && c.role;
  });
  const allSmeDatesValid = smeRows.every((row) => {
    const key = row.Email?.trim().toLowerCase() || "";
    const dates = smeDates[key] || {};
    return dates.start && dates.end && dates.start <= dates.end;
  });

  const advanceMasterSubStep = () => {
    if (masterSubStep === "conflicts") {
      if (masterMismatches.length > 0) {
        setMasterSubStep("role-mismatch");
        return;
      }
      if (smeRows.length > 0) {
        setMasterSubStep("sme-dates");
        return;
      }
      setMasterSubStep("ready");
      setSectionStatus((s) => ({ ...s, masters: "pending" }));
    } else if (masterSubStep === "role-mismatch") {
      if (smeRows.length > 0) {
        setMasterSubStep("sme-dates");
        return;
      }
      setMasterSubStep("ready");
      setSectionStatus((s) => ({ ...s, masters: "pending" }));
    } else if (masterSubStep === "sme-dates") {
      setMasterSubStep("ready");
      setSectionStatus((s) => ({ ...s, masters: "pending" }));
    }
  };

  // ← CHANGED: level mismatches also block import
  const canImport =
    (detected?.hasMasters
      ? (masterSubStep === "ready" ||
          (masterConflicts.length === 0 &&
            masterMismatches.length === 0 &&
            smeRows.length === 0)) &&
        masterLevelMismatches.length === 0
      : true) && (detected?.hasOrgDocs ? !!orgDocsScanResult : true);

  // ─── Run imports ──────────────────────────────────────────────────────────

  const runImports = async () => {
    setPhase("importing");
    const currentOrgId = orgId || profile?.organization_id;
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const allResults = {};

    if (detected?.hasMasters && mastersData) {
      setSectionStatus((s) => ({ ...s, masters: "running" }));
      try {
        const resolutionMap = buildResolutionMap(
          masterConflicts,
          masterResolutions,
        );
        const gr = await insertGroups(
          mastersData.masters,
          resolutionMap,
          existingGroups,
          user,
          currentOrgId,
        );
        const rr = await insertRoles(
          mastersData.roles,
          resolutionMap,
          gr.groupMap,
          existingRoles,
          user,
          currentOrgId,
        );
        const lr = await insertLevels(
          mastersData.levels,
          resolutionMap,
          gr.groupMap,
          existingLevels,
          user,
          currentOrgId,
        );
        const btr = await insertBurdenTypes(
          mastersData.masters,
          existingBurdenTypes,
          user,
          currentOrgId,
        );
        const dr = await insertDepartments(
          mastersData.masters,
          resolutionMap,
          existingDepartments,
          user,
          currentOrgId,
        );
        const elr = await insertExperienceLevels(
          mastersData.masters,
          resolutionMap,
          existingExperienceLevels,
          user,
          currentOrgId,
        );
        const pr = await insertPersonnel(
          effectivePersonnel,
          gr.groupMap,
          rr.roleMap,
          lr.levelMap,
          dr.departmentMap,
          elr.experienceLevelMap,
          resolutionMap,
          smeDates,
          currentOrgId,
        );
        const discR = await insertDiscounts(mastersData.discount, currentOrgId);
        const burnR = await insertBurdens(
          mastersData.burden,
          btr.burdenTypeMap,
          user,
          currentOrgId,
        );
        allResults.masters = { gr, rr, lr, btr, dr, elr, pr, discR, burnR };
        setSectionStatus((s) => ({ ...s, masters: "done" }));
      } catch (err) {
        allResults.masters = { error: err.message };
        setSectionStatus((s) => ({ ...s, masters: "error" }));
      }
    }

    if (detected?.hasResumes && resumesZipBlob) {
      setSectionStatus((s) => ({ ...s, resumes: "running" }));
      try {
        const resumeFile = new File([resumesZipBlob], "resumes.zip", {
          type: "application/zip",
        });
        const response = await uploadBulkResumes(resumeFile, currentOrgId);
        allResults.resumes = response;
        setSectionStatus((s) => ({ ...s, resumes: "done" }));
      } catch (err) {
        allResults.resumes = {
          error: err?.response?.data?.error || err.message,
        };
        setSectionStatus((s) => ({ ...s, resumes: "error" }));
      }
    }

    if (detected?.hasOrgDocs && orgDocsScanResult) {
      setSectionStatus((s) => ({ ...s, orgDocs: "running" }));
      try {
        const { data, error } = await supabase.functions.invoke(
          "bulk-approve",
          {
            body: {
              bulkId: orgDocsScanResult.bulkId,
              orgId: orgId || profile?.organization_id,
            },
          },
        );
        if (error) throw new Error(error.message);
        allResults.orgDocs = { ...data, scanResult: orgDocsScanResult };
        setSectionStatus((s) => ({ ...s, orgDocs: "done" }));
      } catch (err) {
        allResults.orgDocs = { error: err.message };
        setSectionStatus((s) => ({ ...s, orgDocs: "error" }));
      }
    }

    setResults(allResults);
    setPhase("done");
    toast.success("Global upload complete!");
  };

  // ─── Reset ────────────────────────────────────────────────────────────────

  const handleClose = () => {
    if (phase === "importing") return;
    setZipFile(null);
    setParsing(false);
    setDetected(null);
    setMastersData(null);
    setMasterConflicts([]);
    setMasterResolutions({});
    setMasterMismatches([]);
    setMasterLevelMismatches([]);
    setMasterRoleCorrections({});
    setSmeDates({});
    setOrgDocsScanResult(null);
    setResumesZipBlob(null);
    setSectionStatus({
      masters: "pending",
      resumes: "pending",
      orgDocs: "pending",
    });
    setPhase("upload");
    setResults({});
    setMasterSubStep("conflicts");
    onClose();
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-xl flex flex-col h-[90vh] overflow-hidden">
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-5 border-b bg-gradient-to-r from-violet-50 to-indigo-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center">
              <Globe size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Global Combined Upload</h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={phase === "importing"}
            className="p-2 hover:bg-white/70 rounded-lg transition-colors disabled:opacity-40"
          >
            <X size={20} />
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* ── UPLOAD ── */}
          {phase === "upload" && (
            <>
              <label
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                className="border-2 border-dashed border-blue-200 rounded-2xl min-h-[260px] flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all"
              >
                <input
                  type="file"
                  hidden
                  accept=".zip"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFilePick(f);
                  }}
                />
                {parsing ? (
                  <>
                    <Loader2
                      size={40}
                      className="text-blue-400 animate-spin mb-3"
                    />
                    <p className="text-sm text-muted-foreground">
                      Parsing ZIP…
                    </p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mb-4">
                      <UploadCloud size={32} className="text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold mb-1">
                      Drop your combined ZIP here
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      or click to browse
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-3">
                      Max 200 MB · .zip only
                    </p>
                  </>
                )}
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  {
                    icon: BookOpen,
                    color: "blue",
                    folder: "masters",
                    desc: "One .xlsx file using the Masters template",
                  },
                  {
                    icon: Users,
                    color: "emerald",
                    folder: "resumes",
                    desc: "PDF/DOCX resumes for AI extraction",
                  },
                  {
                    icon: FileText,
                    color: "violet",
                    folder: "org-docs",
                    desc: "PDFs/docs for global knowledge base",
                  },
                ].map(({ icon: Icon, color, folder, desc }) => (
                  <div
                    key={folder}
                    className={`border rounded-xl p-3 bg-${color}-50/40 border-${color}-200`}
                  >
                    <div
                      className={`w-7 h-7 rounded-lg bg-${color}-100 flex items-center justify-center mb-2`}
                    >
                      <Icon size={14} className={`text-${color}-600`} />
                    </div>
                    <p className="text-xs font-semibold text-foreground font-mono">
                      {folder}/
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <GlobalUploadGuideButton />
                {zipFile && (
                  <div className="flex items-center gap-3 border border-violet-100 rounded-lg px-4 py-2 bg-violet-50/40">
                    <div>
                      <p className="text-xs font-medium text-violet-700">
                        {zipFile.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(zipFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={() => setZipFile(null)}
                      className="text-red-500 hover:underline text-xs"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── REVIEW ── */}
          {phase === "review" && detected && (
            <>
              <div className="grid grid-cols-3 gap-3">
                {[
                  {
                    key: "masters",
                    label: "Masters & Personnel",
                    icon: BookOpen,
                    color: "blue",
                  },
                  {
                    key: "resumes",
                    label: "Bulk Resumes",
                    icon: Users,
                    color: "emerald",
                  },
                  {
                    key: "orgDocs",
                    label: "Org Documents",
                    icon: FileText,
                    color: "violet",
                  },
                ].map(({ key, label, icon: Icon, color }) => (
                  <div
                    key={key}
                    className={`border rounded-xl p-3 text-center ${sectionStatus[key] === "skipped" ? "border-gray-200 bg-gray-50 opacity-40" : `border-${color}-200 bg-${color}-50/30`}`}
                  >
                    <Icon
                      size={18}
                      className={`mx-auto mb-1 ${sectionStatus[key] === "skipped" ? "text-gray-400" : `text-${color}-600`}`}
                    />
                    <p className="text-xs font-medium">{label}</p>
                    <div className="flex justify-center mt-1">
                      <StatusBadge status={sectionStatus[key]} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Masters review */}
              {detected.hasMasters && mastersData && (
                <SectionCard
                  icon={BookOpen}
                  title="Masters & Personnel Review"
                  color="blue"
                  status={sectionStatus.masters}
                >
                  {/* ← CHANGED: Level mismatch error banner — blocks import, listed per person */}
                  {masterLevelMismatches.length > 0 && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 mb-3">
                      <AlertCircle
                        size={13}
                        className="text-red-500 mt-0.5 flex-shrink-0"
                      />
                      <div className="text-xs text-red-700">
                        <strong>
                          {masterLevelMismatches.length} level mismatch
                          {masterLevelMismatches.length !== 1 ? "es" : ""} found
                        </strong>{" "}
                        — these cannot be fixed here. Please correct your XLSX
                        and re-upload.
                        <ul className="mt-1.5 list-disc list-inside space-y-1">
                          {masterLevelMismatches.map((m) => (
                            <li key={m.idx}>
                              <strong>{m.name}</strong>: level{" "}
                              <span className="font-mono bg-red-100 px-1 rounded">
                                "{m.originalLevel}"
                              </span>{" "}
                              is not valid for group{" "}
                              <span className="font-mono bg-red-100 px-1 rounded">
                                "{m.originalGroup}"
                              </span>
                              {m.validLevelsForGroup.length > 0 && (
                                <>
                                  {" "}
                                  — valid options:{" "}
                                  {m.validLevelsForGroup.join(", ")}
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Conflicts step */}
                  {masterSubStep === "conflicts" &&
                    masterConflicts.length > 0 && (
                      <>
                        <ConflictResolver
                          conflicts={masterConflicts}
                          resolutions={masterResolutions}
                          onChange={(id, val) =>
                            setMasterResolutions((r) => ({ ...r, [id]: val }))
                          }
                        />
                        {/* ← CHANGED: role mismatch banner shown inline during conflict step too */}
                        {masterMismatches.length > 0 && (
                          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 mb-3">
                            <AlertTriangle
                              size={13}
                              className="text-amber-500 mt-0.5 flex-shrink-0"
                            />
                            <p className="text-xs text-amber-700">
                              <strong>
                                {masterMismatches.length} personnel row
                                {masterMismatches.length !== 1
                                  ? "s have"
                                  : " has"}{" "}
                                a role that doesn't match their group.
                              </strong>{" "}
                              You'll fix them on the next step.
                            </p>
                          </div>
                        )}
                        {smeRows.length > 0 && (
                          <div className="flex items-start gap-2 p-3 rounded-lg bg-purple-50 border border-purple-200 mb-3">
                            <Calendar
                              size={13}
                              className="text-purple-500 mt-0.5 flex-shrink-0"
                            />
                            <p className="text-xs text-purple-700">
                              <strong>
                                {smeRows.length} SME
                                {smeRows.length !== 1 ? "s" : ""} detected
                              </strong>{" "}
                              — collaboration dates required on a later step.
                            </p>
                          </div>
                        )}
                        <button
                          onClick={advanceMasterSubStep}
                          disabled={
                            !allConflictsResolved ||
                            masterLevelMismatches.length > 0
                          }
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium disabled:opacity-50"
                        >
                          Next <ChevronRight size={13} />
                        </button>
                      </>
                    )}

                  {/* Role mismatch step */}
                  {masterSubStep === "role-mismatch" &&
                    masterMismatches.length > 0 && (
                      <>
                        <RoleMismatchStep
                          mismatches={masterMismatches}
                          roleSheetRows={mastersData.roles}
                          corrections={masterRoleCorrections}
                          onCorrect={(idx, c) =>
                            setMasterRoleCorrections((r) => ({
                              ...r,
                              [idx]: c,
                            }))
                          }
                        />
                        <button
                          onClick={advanceMasterSubStep}
                          disabled={!allMismatchesFixed}
                          className="mt-3 flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium disabled:opacity-50"
                        >
                          {smeRows.length > 0 ? "Next: SME Dates" : "Confirm"}{" "}
                          <ChevronRight size={13} />
                        </button>
                      </>
                    )}

                  {/* SME dates step */}
                  {masterSubStep === "sme-dates" && smeRows.length > 0 && (
                    <>
                      <SmeDateStep
                        smeRows={smeRows}
                        smeDates={smeDates}
                        onDateChange={(key, field, val) =>
                          setSmeDates((d) => ({
                            ...d,
                            [key]: {
                              ...(d[key] || { start: "", end: "" }),
                              [field]: val,
                            },
                          }))
                        }
                      />
                      <button
                        onClick={advanceMasterSubStep}
                        disabled={!allSmeDatesValid}
                        className="mt-3 flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium disabled:opacity-50"
                      >
                        Confirm SME Dates <ChevronRight size={13} />
                      </button>
                    </>
                  )}

                  {/* Ready state */}
                  {(masterSubStep === "ready" ||
                    (masterConflicts.length === 0 &&
                      masterMismatches.length === 0 &&
                      smeRows.length === 0)) &&
                    masterLevelMismatches.length === 0 && (
                      <div className="flex items-center gap-2 text-xs text-green-700">
                        <CheckCircle2 size={14} />
                        <span>
                          {mastersData.masters.length} master rows ·{" "}
                          {mastersData.personnel.length} personnel ·{" "}
                          {mastersData.discount.length} discounts ·{" "}
                          {mastersData.burden.length} burden rows ready
                        </span>
                      </div>
                    )}
                </SectionCard>
              )}

              {/* Resumes review */}
              {detected.hasResumes && (
                <SectionCard
                  icon={Users}
                  title="Bulk Resumes"
                  color="emerald"
                  status={sectionStatus.resumes}
                >
                  <div className="flex items-center gap-2 text-xs text-emerald-700">
                    <CheckCircle2 size={14} />
                    <span>
                      Resumes ZIP ready — will be sent to AI extraction on
                      import
                    </span>
                  </div>
                </SectionCard>
              )}

              {/* Org docs review */}
              {detected.hasOrgDocs && (
                <SectionCard
                  icon={FileText}
                  title="Org Documents"
                  color="violet"
                  status={sectionStatus.orgDocs}
                >
                  {orgDocsScanResult ? (
                    <>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className="border rounded-lg p-3 bg-green-50 flex items-center gap-2">
                          <CheckCircle2 size={14} className="text-green-600" />
                          <div>
                            <p className="text-xs text-muted-foreground">
                              Valid
                            </p>
                            <p className="text-lg font-bold text-green-700">
                              {orgDocsScanResult.valid}
                            </p>
                          </div>
                        </div>
                        <div className="border rounded-lg p-3 bg-red-50 flex items-center gap-2">
                          <AlertCircle size={14} className="text-red-600" />
                          <div>
                            <p className="text-xs text-muted-foreground">
                              Invalid
                            </p>
                            <p className="text-lg font-bold text-red-700">
                              {orgDocsScanResult.invalid}
                            </p>
                          </div>
                        </div>
                      </div>
                      <OrgDocsFileTable files={orgDocsScanResult.files} />
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 size={13} className="animate-spin" /> Scanning
                      documents…
                    </div>
                  )}
                </SectionCard>
              )}
            </>
          )}

          {/* ── IMPORTING ── */}
          {phase === "importing" && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground mb-1">
                Importing all sections…
              </p>
              {[
                {
                  key: "masters",
                  label: "Masters & Personnel",
                  icon: BookOpen,
                },
                { key: "resumes", label: "Bulk Resumes", icon: Users },
                { key: "orgDocs", label: "Org Documents", icon: FileText },
              ].map(
                ({ key, label, icon: Icon }) =>
                  sectionStatus[key] !== "skipped" && (
                    <div
                      key={key}
                      className="flex items-center justify-between border rounded-lg p-3"
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={15} className="text-muted-foreground" />
                        <span className="text-sm">{label}</span>
                      </div>
                      <StatusBadge status={sectionStatus[key]} />
                    </div>
                  ),
              )}
            </div>
          )}

          {/* ── DONE ── ← CHANGED: full detailed result */}
          {phase === "done" && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={20} className="text-green-500" />
                <p className="text-sm font-semibold">Global upload complete</p>
              </div>

              {/* Masters — detailed */}
              {results.masters && !results.masters.error && (
                <MastersResultCard r={results.masters} />
              )}
              {results.masters?.error && (
                <div className="border rounded-xl p-3 bg-red-50 border-red-200">
                  <p className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1.5">
                    <BookOpen size={13} /> Masters — Error
                  </p>
                  <p className="text-xs text-red-600">
                    {results.masters.error}
                  </p>
                </div>
              )}

              {/* Resumes — detailed */}
              {results.resumes && !results.resumes.error && (
                <div className="border rounded-xl p-4 bg-emerald-50/40 border-emerald-200">
                  <p className="text-xs font-semibold text-emerald-700 mb-3 flex items-center gap-1.5">
                    <Users size={13} /> Bulk Resumes
                  </p>
                  <div className="bg-white/70 border border-emerald-100 rounded-lg px-3 py-2 mb-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">
                        Resumes Parsed
                      </p>
                      <p className="text-lg font-bold text-emerald-700">
                        {results.resumes.total ?? results.resumes.queued ?? "—"}
                      </p>
                    </div>
                    {(results.resumes.failed ?? 0) > 0 && (
                      <SkipReasons
                        items={[
                          `${results.resumes.failed} file(s) could not be read — check format (PDF/DOCX only)`,
                        ]}
                      />
                    )}
                    {(results.resumes.duplicate ?? 0) > 0 && (
                      <SkipReasons
                        items={[
                          `${results.resumes.duplicate} skipped — already uploaded for this organisation`,
                        ]}
                      />
                    )}
                  </div>
                </div>
              )}
              {results.resumes?.error && (
                <div className="border rounded-xl p-3 bg-red-50 border-red-200">
                  <p className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1.5">
                    <Users size={13} /> Resumes — Error
                  </p>
                  <p className="text-xs text-red-600">
                    {results.resumes.error}
                  </p>
                </div>
              )}

              {/* Org docs — detailed */}
              {results.orgDocs && !results.orgDocs.error && (
                <div className="border rounded-xl p-4 bg-violet-50/40 border-violet-200">
                  <p className="text-xs font-semibold text-violet-700 mb-3 flex items-center gap-1.5">
                    <FileText size={13} /> Org Documents
                  </p>
                  {results.orgDocs.scanResult && (
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-white/70 border border-violet-100 rounded-lg px-3 py-2">
                        <p className="text-xs text-muted-foreground">
                          Approved
                        </p>
                        <p className="text-lg font-bold text-green-700">
                          {results.orgDocs.scanResult.valid ?? "—"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          added to knowledge base
                        </p>
                      </div>
                      <div className="bg-white/70 border border-violet-100 rounded-lg px-3 py-2">
                        <p className="text-xs text-muted-foreground">
                          Rejected
                        </p>
                        <p className="text-lg font-bold text-red-700">
                          {results.orgDocs.scanResult.invalid ?? "—"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          failed validation
                        </p>
                      </div>
                    </div>
                  )}
                  {results.orgDocs.scanResult?.invalid > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-red-700 mb-1.5 flex items-center gap-1">
                        <AlertTriangle size={11} /> Invalid files were not
                        approved — fix and re-upload separately
                      </p>
                      <OrgDocsFileTable
                        files={(results.orgDocs.scanResult.files || []).filter(
                          (f) => f.status !== "VALID",
                        )}
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-green-700">
                    <CheckCircle2 size={13} /> Valid documents approved and
                    indexed successfully
                  </div>
                </div>
              )}
              {results.orgDocs?.error && (
                <div className="border rounded-xl p-3 bg-red-50 border-red-200">
                  <p className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1.5">
                    <FileText size={13} /> Org Docs — Error
                  </p>
                  <p className="text-xs text-red-600">
                    {results.orgDocs.error}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="border-t px-6 py-4 flex items-center justify-between flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={phase === "importing"}
            className="px-4 py-2 border rounded-lg text-sm hover:bg-muted/40 transition disabled:opacity-40"
          >
            {phase === "done" ? "Close" : "Cancel"}
          </button>

          {phase === "review" && (
            <div className="flex items-center gap-2">
              {/* ← CHANGED: tooltip-like hint when level mismatches block import */}
              {masterLevelMismatches.length > 0 && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle size={12} /> Fix level mismatches in your XLSX
                  first
                </p>
              )}
              <button
                onClick={runImports}
                disabled={!canImport}
                className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground hover:opacity-90 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
              >
                <Upload size={15} /> Import Everything
              </button>
            </div>
          )}

          {phase === "importing" && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 size={15} className="animate-spin" /> Importing, please
              wait…
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Master import helpers (self-contained, same logic as MasterUploadModal) ──

const insertGroups = async (
  masters,
  resolutionMap,
  existingGroupsList,
  user,
  orgId,
) => {
  const errors = [];
  let groupsCreated = 0,
    groupsSkipped = 0;
  const groupMap = Object.fromEntries(
    (existingGroupsList || []).map((g) => [
      String(g.name).trim().toLowerCase(),
      g.id,
    ]),
  );
  for (const row of masters) {
    if (!row.group) continue;
    const key = masterKeyFor(row.group, "group", resolutionMap);
    const name = masterNameFor(row.group, "group", resolutionMap);
    if (!groupMap[key]) {
      const costMin =
        row.cost_min !== "" && row.cost_min != null
          ? Number(row.cost_min)
          : 1000;
      const costMax =
        row.cost_max !== "" && row.cost_max != null
          ? Number(row.cost_max)
          : 1000;
      const { data: insertedGroup, error } = await supabase
        .from("md_groups")
        .insert({
          name,
          organization_id: orgId,
          cost_min: isNaN(costMin) ? 1000 : costMin,
          cost_max: isNaN(costMax) ? 1000 : costMax,
          currency_id: 5,
          timeline_id: null,
          created_by: user?.id,
        })
        .select("id")
        .single();
      if (error) {
        errors.push(`Group "${row.group}": ${error.message}`);
      } else {
        groupMap[key] = insertedGroup.id;
        groupsCreated++;
      }
    } else {
      groupsSkipped++;
    }
  }
  return { groupMap, groupsCreated, groupsSkipped, errors };
};

const insertRoles = async (
  roles,
  resolutionMap,
  groupMap,
  existingRolesList,
  user,
  orgId,
) => {
  const errors = [];
  let rolesCreated = 0,
    rolesSkipped = 0;
  const roleMap = Object.fromEntries(
    (existingRolesList || []).map((r) => [
      String(r.name).trim().toLowerCase(),
      r.id,
    ]),
  );
  for (const row of roles) {
    if (!row.Role) continue;
    const roleKey = masterKeyFor(row.Role, "Role", resolutionMap);
    const roleName = masterNameFor(row.Role, "Role", resolutionMap);
    const groupKey = row.Group
      ? masterKeyFor(row.Group, "group", resolutionMap)
      : null;
    const groupId = groupKey ? (groupMap[groupKey] ?? null) : null;
    if (!roleMap[roleKey]) {
      if (!groupId) {
        errors.push(`Role "${row.Role}": no matching group`);
        continue;
      }
      const { data: insertedRole, error } = await supabase
        .from("md_roles")
        .insert({
          name: roleName,
          organization_id: orgId,
          group_id: groupId,
          created_by: user?.id,
        })
        .select("id")
        .single();
      if (error) {
        errors.push(`Role "${row.Role}": ${error.message}`);
      } else {
        roleMap[roleKey] = insertedRole.id;
        rolesCreated++;
      }
    } else {
      rolesSkipped++;
    }
  }
  return { roleMap, rolesCreated, rolesSkipped, errors };
};

const insertLevels = async (
  levels,
  resolutionMap,
  groupMap,
  existingLevelsList,
  user,
  orgId,
) => {
  const errors = [];
  let levelsCreated = 0,
    levelsSkipped = 0;
  const levelMap = Object.fromEntries(
    (existingLevelsList || []).map((l) => [
      `${l.group_id}::${String(l.name).trim().toLowerCase()}`,
      l.id,
    ]),
  );
  for (const row of levels) {
    if (!row.Level && row.Level !== 0) continue;
    const levelName = String(row.Level).trim();
    if (!levelName) continue;
    const groupKey = row.Group
      ? masterKeyFor(row.Group, "group", resolutionMap)
      : null;
    const currentGroupId = groupKey ? (groupMap[groupKey] ?? null) : null;
    if (!currentGroupId) {
      errors.push(`Level "${levelName}": no matching group`);
      continue;
    }
    const levelKey = `${currentGroupId}::${levelName.toLowerCase()}`;
    if (!levelMap[levelKey]) {
      const rawCost = row.Cost;
      const cost = rawCost !== "" && rawCost != null ? Number(rawCost) : 1000;
      const { data: insertedLevel, error } = await supabase
        .from("md_levels")
        .insert({
          name: levelName,
          group_id: currentGroupId,
          costing: isNaN(cost) ? 1000 : cost,
          organization_id: orgId,
          created_by: user?.id,
        })
        .select("id")
        .single();
      if (error) {
        errors.push(`Level "${levelName}": ${error.message}`);
      } else {
        levelMap[levelKey] = insertedLevel.id;
        levelsCreated++;
      }
    } else {
      levelsSkipped++;
    }
  }
  return { levelMap, levelsCreated, levelsSkipped, errors };
};

const insertBurdenTypes = async (
  masters,
  existingBurdenTypesList,
  user,
  orgId,
) => {
  const errors = [];
  let burdenTypesCreated = 0,
    burdenTypesSkipped = 0;
  const burdenTypeMap = Object.fromEntries(
    (existingBurdenTypesList || []).map((t) => [
      String(t.name).trim().toLowerCase(),
      { id: t.id, unit: t.unit || "pct" },
    ]),
  );
  for (const row of masters) {
    if (!row.burden_type) continue;
    const rawName = String(row.burden_type).trim();
    if (!rawName) continue;
    const typeKey = rawName.toLowerCase();
    const unit = normalizeUnit(row["burden_type Unit"]);
    if (!burdenTypeMap[typeKey]) {
      const { data: insertedType, error } = await supabase
        .from("md_burden_type")
        .insert({ name: rawName, unit, description: null, org_id: orgId })
        .select("id")
        .single();
      if (error) {
        errors.push(`Burden type "${rawName}": ${error.message}`);
      } else {
        burdenTypeMap[typeKey] = { id: insertedType.id, unit };
        burdenTypesCreated++;
      }
    } else {
      burdenTypesSkipped++;
    }
  }
  return { burdenTypeMap, burdenTypesCreated, burdenTypesSkipped, errors };
};

const insertDepartments = async (
  masters,
  resolutionMap,
  existingDepartmentsList,
  user,
  orgId,
) => {
  const errors = [];
  let departmentsCreated = 0,
    departmentsSkipped = 0;
  const departmentMap = Object.fromEntries(
    (existingDepartmentsList || []).map((d) => [
      String(d.name).trim().toLowerCase(),
      d.id,
    ]),
  );
  for (const row of masters) {
    if (!row.department) continue;
    const key = masterKeyFor(row.department, "department", resolutionMap);
    const name = masterNameFor(row.department, "department", resolutionMap);
    if (!departmentMap[key]) {
      const { data: insertedDept, error } = await supabase
        .from("md_department")
        .insert({ name, organization_id: orgId, created_by: user?.id })
        .select("id")
        .single();
      if (error) {
        errors.push(`Dept "${row.department}": ${error.message}`);
      } else {
        departmentMap[key] = insertedDept.id;
        departmentsCreated++;
      }
    } else {
      departmentsSkipped++;
    }
  }
  return { departmentMap, departmentsCreated, departmentsSkipped, errors };
};

const insertExperienceLevels = async (
  masters,
  resolutionMap,
  existingExperienceLevelsList,
  user,
  orgId,
) => {
  const errors = [];
  let experienceLevelsCreated = 0,
    experienceLevelsSkipped = 0;
  const experienceLevelMap = Object.fromEntries(
    (existingExperienceLevelsList || []).map((x) => [
      String(x.name).trim().toLowerCase(),
      x.id,
    ]),
  );
  for (const row of masters) {
    if (!row.experience_level) continue;
    const key = masterKeyFor(
      row.experience_level,
      "experience_level",
      resolutionMap,
    );
    const name = masterNameFor(
      row.experience_level,
      "experience_level",
      resolutionMap,
    );
    if (!experienceLevelMap[key]) {
      const { data: insertedExpLevel, error } = await supabase
        .from("md_experience_level")
        .insert({ name, organization_id: orgId, created_by: user?.id })
        .select("id")
        .single();
      if (error) {
        errors.push(`Exp level "${row.experience_level}": ${error.message}`);
      } else {
        experienceLevelMap[key] = insertedExpLevel.id;
        experienceLevelsCreated++;
      }
    } else {
      experienceLevelsSkipped++;
    }
  }
  return {
    experienceLevelMap,
    experienceLevelsCreated,
    experienceLevelsSkipped,
    errors,
  };
};

const insertPersonnel = async (
  personnel,
  groupMap,
  roleMap,
  levelMap,
  departmentMap,
  experienceLevelMap,
  resolutionMap,
  smeDates,
  orgId,
) => {
  if (!personnel?.length)
    return {
      inserted: 0,
      skipped: 0,
      skippedMissing: 0,
      skippedDuplicate: 0,
      errors: [],
    };
  const errors = [];
  let inserted = 0,
    skippedMissing = 0,
    skippedDuplicate = 0;
  const { data: existingPersonnel } = await supabase
    .from("personnel")
    .select("email")
    .eq("is_active", 0);
  const existingEmails = new Set(
    (existingPersonnel || []).map((p) => p.email?.toLowerCase()),
  );
  for (const row of personnel) {
    if (!row.Name || !row.Email) {
      skippedMissing++;
      continue;
    }
    if (existingEmails.has(row.Email.trim().toLowerCase())) {
      skippedDuplicate++;
      continue;
    }
    const groupKey = row.Group
      ? masterKeyFor(row.Group, "group", resolutionMap)
      : null;
    const roleKey = row.Role
      ? masterKeyFor(row.Role, "Role", resolutionMap)
      : null;
    const groupId = groupKey ? (groupMap?.[groupKey] ?? null) : null;
    const roleId = roleKey ? (roleMap?.[roleKey] ?? null) : null;
    let levelId = null;
    if (row.Level && groupId) {
      const lKey = `${groupId}::${String(row.Level).trim().toLowerCase()}`;
      levelId = levelMap?.[lKey] ?? null;
    }
    let departmentId = null;
    if (row.Department) {
      const dKey = masterKeyFor(row.Department, "department", resolutionMap);
      departmentId = departmentMap?.[dKey] ?? null;
    }
    let experienceLevelId = null;
    if (row.Experience_level) {
      const xKey = masterKeyFor(
        row.Experience_level,
        "experience_level",
        resolutionMap,
      );
      experienceLevelId = experienceLevelMap?.[xKey] ?? null;
    }
    const isSme = isSmeRow(row);
    const emailKey = row.Email.trim().toLowerCase();
    const collabDates = isSme ? smeDates[emailKey] || {} : {};
    const payload = {
      name: row.Name.trim(),
      email: row.Email.trim(),
      phone: String(row.Phone || ""),
      position: "",
      department_id: departmentId,
      experience_level_id: experienceLevelId,
      group_id: groupId,
      role_id: roleId,
      level_id: levelId,
      skills: [],
      certifications: [],
      organization_id: orgId,
      user_category_id: isSme ? 2 : 1,
      collaborator_start_date: isSme ? collabDates.start || null : null,
      collaborator_end_date: isSme ? collabDates.end || null : null,
      avatar_url: "",
    };
    try {
      const { createPersonnel } = await import("services/personnelService");
      await createPersonnel(payload);
      inserted++;
      existingEmails.add(emailKey);
    } catch (err) {
      errors.push(`"${row.Name}": ${err.message}`);
    }
  }
  return {
    inserted,
    skipped: skippedMissing + skippedDuplicate,
    skippedMissing,
    skippedDuplicate,
    errors,
  };
};

const insertDiscounts = async (discountRows, orgId) => {
  if (!discountRows?.length)
    return {
      inserted: 0,
      profilesCreated: 0,
      skippedDuplicate: 0,
      skippedMissing: 0,
      errors: [],
    };

  const errors = [];
  let inserted = 0,
    profilesCreated = 0,
    skippedDuplicate = 0,
    skippedMissing = 0;

  // ── Fetch all existing discount profiles for this org ──
  const { data: profiles, error: profilesError } = await supabase
    .from("ms_discount_profile")
    .select("id, type")
    .eq("organization_id", orgId);

  if (profilesError)
    return {
      inserted,
      profilesCreated,
      skippedDuplicate,
      skippedMissing,
      errors: [profilesError.message],
    };

  // Map: profile type (lowercased) → id
  const profileNameMap = Object.fromEntries(
    (profiles || []).map((p) => [
      String(p.type ?? "")
        .trim()
        .toLowerCase(),
      p.id,
    ]),
  );

  // ── Fetch ALL existing head discount rows (no is_active filter) ──
  const { data: existingHeads } = await supabase
    .from("ms_head_discount_profile")
    .select("discount_id, discount_type, discount_percent")
    .eq("organization_id", orgId);

  // Composite dup key: profileId::discountName(lowercased)
  // This matches how we insert: discount_type = discountName
  const existingKeys = new Set(
    (existingHeads || []).map(
      (h) =>
        `${h.discount_id}::${String(h.discount_type ?? "")
          .trim()
          .toLowerCase()}`,
    ),
  );

  for (const row of discountRows) {
    const discountName = String(row.Discount_name || "").trim();
    const discountType = String(row.Discount_type || "").trim();
    const discountPercent = Number(row.Discount_percent);

    if (!discountName || !discountType || isNaN(discountPercent)) {
      skippedMissing++;
      continue;
    }

    // ── Get or create the discount profile (keyed by Discount_type) ──
    const profileKey = discountType.toLowerCase();
    let discountId = profileNameMap[profileKey];

    if (!discountId) {
      const { data: newProfile, error } = await supabase
        .from("ms_discount_profile")
        .insert({
          organization_id: orgId,
          type: discountType,
          description: null,
          is_active: 0,
        })
        .select("id")
        .single();

      if (error) {
        errors.push(`Discount profile "${discountType}": ${error.message}`);
        continue;
      }
      discountId = newProfile.id;
      profileNameMap[profileKey] = discountId;
      profilesCreated++;
    }

    // ── Dup check: profileId + discountName (what we store in discount_type col) ──
    const dupKey = `${discountId}::${discountName.toLowerCase()}`;
    if (existingKeys.has(dupKey)) {
      skippedDuplicate++;
      continue;
    }

    const { error } = await supabase.from("ms_head_discount_profile").insert({
      organization_id: orgId,
      discount_id: discountId,
      discount_type: discountName, // ← stores the name, matches dup key above
      discount_percent: discountPercent,
    });

    if (error) {
      errors.push(`Discount head "${discountName}": ${error.message}`);
    } else {
      inserted++;
      existingKeys.add(dupKey); // prevent in-batch duplicates too
    }
  }

  return {
    inserted,
    profilesCreated,
    skippedDuplicate,
    skippedMissing,
    errors,
  };
};

const RESERVED_BURDEN_COLUMNS = new Set(["name", "desc"]);

const insertBurdens = async (burdenRows, burdenTypeMap, user, orgId) => {
  if (!burdenRows?.length)
    return {
      inserted: 0,
      burdensCreated: 0,
      skippedDuplicate: 0,
      skippedMissing: 0,
      skippedUnknownColumn: 0,
      errors: [],
    };
  const errors = [];
  let inserted = 0,
    burdensCreated = 0,
    skippedDuplicate = 0,
    skippedMissing = 0,
    skippedUnknownColumn = 0;
  const { data: existingBurdens, error: burdensError } = await supabase
    .from("md_burdens")
    .select("id, name")
    .eq("org_id", orgId);
  if (burdensError)
    return {
      inserted,
      burdensCreated,
      skippedDuplicate,
      skippedMissing,
      skippedUnknownColumn,
      errors: [burdensError.message],
    };
  const burdenNameMap = Object.fromEntries(
    (existingBurdens || []).map((b) => [
      String(b.name ?? "")
        .trim()
        .toLowerCase(),
      b.id,
    ]),
  );
  const burdenIds = (existingBurdens || []).map((b) => b.id);
  let existingKeys = new Set();
  if (burdenIds.length > 0) {
    const { data: existingMappings } = await supabase
      .from("md_burden_type_mapping")
      .select("burden_id, burden_type_id")
      .in("burden_id", burdenIds)
      .eq("is_active", 0);
    existingKeys = new Set(
      (existingMappings || []).map(
        (m) => `${m.burden_id}::${m.burden_type_id}`,
      ),
    );
  }
  for (const row of burdenRows) {
    const burdenName = String(row.Name || "").trim();
    if (!burdenName) {
      skippedMissing++;
      continue;
    }
    const description = String(row.Desc || "").trim();
    const burdenKey = burdenName.toLowerCase();
    let burdenId = burdenNameMap[burdenKey];

    if (!burdenId) {
      const { data: newBurden, error } = await supabase
        .from("md_burdens")
        .insert({
          name: burdenName,
          description: description || null,
          org_id: orgId,
          created_by: user?.id,
        })
        .select("id")
        .single();
      if (error) {
        errors.push(`Burden "${burdenName}": ${error.message}`);
        continue;
      }
      burdenId = newBurden.id;
      burdenNameMap[burdenKey] = burdenId;
      burdensCreated++;
    }
    const mappingsToInsert = [];
    for (const [rawKey, rawValue] of Object.entries(row)) {
      const key = rawKey.trim();
      if (
        RESERVED_BURDEN_COLUMNS.has(key.toLowerCase()) ||
        isPlaceholderColumn(key)
      )
        continue;
      if (rawValue === "" || rawValue == null) continue;
      const value = Number(rawValue);
      if (isNaN(value)) continue;
      const typeEntry = burdenTypeMap[key.toLowerCase()];
      if (!typeEntry) {
        skippedUnknownColumn++;
        errors.push(`Burden "${burdenName}": column "${key}" unknown`);
        continue;
      }
      const dupKey = `${burdenId}::${typeEntry.id}`;
      if (existingKeys.has(dupKey)) {
        skippedDuplicate++;
        console.log(
          `[Burden dup-skip] burden="${burdenName}" col="${key}" burdenId=${burdenId} burdenTypeId=${typeEntry.id} dupKey="${dupKey}"`,
        );
        continue;
      }
      mappingsToInsert.push({
        burden_id: burdenId,
        burden_type_id: typeEntry.id,
        value,
        created_by: user?.id,
      });
      existingKeys.add(dupKey);
    }
    if (mappingsToInsert.length > 0) {
      const { error } = await supabase
        .from("md_burden_type_mapping")
        .insert(mappingsToInsert);
      if (error) {
        errors.push(`Burden "${burdenName}": ${error.message}`);
      } else {
        inserted += mappingsToInsert.length;
      }
    }
  }
  return {
    inserted,
    burdensCreated,
    skippedDuplicate,
    skippedMissing,
    skippedUnknownColumn,
    errors,
  };
};

export default GlobalCombinedUploadModal;
